import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ListTodo } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import {
  useTodoLists,
  useTodoListWorkspaceSummary,
  useTodoAssignmentNotifications,
  useTodoListReorder,
} from '../hooks';
import { useTodoList } from '../hooks/useTodoList';
import { ListCard, CreateListDialog, OtherWorkspacesBanner } from '../components';
import api from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';

/**
 * Holder-scoped list dashboard. The Holder switcher (Personal + each
 * active Org Membership) lives in the page header; switching Holders
 * is how the user moves between personal and team todo lists. There is
 * no separate "shared with me" view — Org Holder membership IS the
 * sharing model in W6.
 */
export function MyListsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    selectedHolderId,
    selectedOption,
    isLoading: switcherLoading,
    options,
    setSelectedHolderId,
  } = useHolderSwitcher();
  const { lists, loading, error } = useTodoLists(selectedHolderId);
  const { summaries: workspaceSummaries } = useTodoListWorkspaceSummary();
  const { countForList } = useTodoAssignmentNotifications();
  const { reorder } = useTodoListReorder(selectedHolderId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  // Only a workspace owner/admin (or the owner of a Personal Holder) can create
  // lists — the server gates the create_list verb to those roles. Plain members
  // and viewers get no "New List" affordance. This just mirrors the server rule.
  // The same gate governs drag-reorder (the reorder_lists verb is owner/admin/self).
  const userRole = selectedOption?.role ?? 'viewer';
  const canCreateList = userRole === 'owner' || userRole === 'admin' || userRole === 'self';
  const canReorder = canCreateList;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = lists.map((l) => l.listId);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    // Persist the FULL new sequence; the hook updates the cache optimistically.
    void reorder(arrayMove(ids, oldIndex, newIndex));
  };

  const activeList = activeDragId ? lists.find((l) => l.listId === activeDragId) : null;

  // Reuse useTodoList only for its strong-typed update path; we don't
  // need it here. Just typecheck-ping it.
  void useTodoList;

  const handleCreate = async (input: { name: string; description?: string; itemKey?: string }) => {
    if (!selectedHolderId) return;
    const result = await api.createTodoList(selectedHolderId, input);
    await queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.my(selectedHolderId) });
    setCreateDialogOpen(false);
    navigate(`/a/arche_todo_lists/${result.list.listId}`);
  };

  if (switcherLoading) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!selectedHolderId) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-muted-foreground">
            Select a workspace to view your todo lists.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_todo_lists" title="Todo Lists">
        {canCreateList && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New List
          </Button>
        )}
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 space-y-6 w-full">
        <OtherWorkspacesBanner
          summaries={workspaceSummaries}
          selectedHolderId={selectedHolderId}
          options={options}
          onSwitch={setSelectedHolderId}
        />
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : lists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ListTodo className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground mb-4">No todo lists yet</p>
            {canCreateList ? (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first list
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Only a workspace owner or admin can create lists.
              </p>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
            <SortableContext items={lists.map((l) => l.listId)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {lists.map((list) => (
                  <ListCard
                    key={list.listId}
                    list={list}
                    unreadCount={countForList(list.listId)}
                    sortable={canReorder}
                    onClick={() => navigate(`/a/arche_todo_lists/${list.listId}`)}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeList ? (
                <div className="rounded-lg border bg-card px-4 py-3 shadow-lg">
                  <span className="font-semibold">{activeList.name}</span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        <CreateListDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onCreate={handleCreate}
        />
      </div>
    </div>
  );
}
